import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/environment';

interface VPSInstance {
  id: string;
  hostname: string;
  name: string;
  status: string;
  backup_enabled: boolean;
  customer_email: string;
  proxmox_vmid: number;
}

interface Snapshot {
  name: string;
  description: string;
  snaptime: number;
  vmstate: boolean;
}

interface BackupStats {
  total_backups: number;
  total_size_mb: number;
  vps_with_backup: number;
  last_backup_run: string | null;
}

const VPSBackupManagement: React.FC = () => {
  const [vpsInstances, setVpsInstances] = useState<VPSInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<VPSInstance | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [backupStats, setBackupStats] = useState<BackupStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCreateSnapshot, setShowCreateSnapshot] = useState(false);
  const [newSnapshotName, setNewSnapshotName] = useState('');
  const [newSnapshotDescription, setNewSnapshotDescription] = useState('');

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchVPSInstances();
    fetchBackupStats();
  }, []);

  const fetchVPSInstances = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/admin/vps/instances'), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        // Filter to only show running VPS instances
        const runningInstances = data.data.filter((vps: VPSInstance) =>
          vps.status === 'running' && vps.proxmox_vmid
        );
        setVpsInstances(runningInstances);
      }
    } catch (err: any) {
      setError('Failed to fetch VPS instances');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupStats = async () => {
    try {
      const response = await fetch(getApiUrl('/admin/vps-backups/stats'), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setBackupStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch backup stats:', err);
    }
  };

  const fetchSnapshots = async (instanceId: string) => {
    try {
      setLoadingSnapshots(true);
      const response = await fetch(getApiUrl(`/admin/vps-backups/${instanceId}/snapshots`), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSnapshots(data.data || []);
      } else {
        setSnapshots([]);
      }
    } catch (err) {
      setSnapshots([]);
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const handleSelectInstance = (instance: VPSInstance) => {
    setSelectedInstance(instance);
    fetchSnapshots(instance.id);
  };

  const handleEnableBackup = async (instanceId: string) => {
    try {
      setActionLoading(`enable-${instanceId}`);
      const response = await fetch(getApiUrl(`/admin/vps-backups/${instanceId}/enable`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Automated backups enabled successfully');
        fetchVPSInstances();
      } else {
        setError(data.error || 'Failed to enable backups');
      }
    } catch (err) {
      setError('Failed to enable backups');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableBackup = async (instanceId: string) => {
    try {
      setActionLoading(`disable-${instanceId}`);
      const response = await fetch(getApiUrl(`/admin/vps-backups/${instanceId}/disable`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Automated backups disabled');
        fetchVPSInstances();
      } else {
        setError(data.error || 'Failed to disable backups');
      }
    } catch (err) {
      setError('Failed to disable backups');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateSnapshot = async () => {
    if (!selectedInstance) return;
    try {
      setActionLoading('create-snapshot');
      const response = await fetch(getApiUrl(`/admin/vps-backups/${selectedInstance.id}/snapshots`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: newSnapshotName || undefined,
          description: newSnapshotDescription || undefined
        })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Snapshot created successfully');
        setShowCreateSnapshot(false);
        setNewSnapshotName('');
        setNewSnapshotDescription('');
        fetchSnapshots(selectedInstance.id);
        fetchBackupStats();
      } else {
        setError(data.error || 'Failed to create snapshot');
      }
    } catch (err) {
      setError('Failed to create snapshot');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreSnapshot = async (snapshotName: string) => {
    if (!selectedInstance) return;
    if (!window.confirm(`Are you sure you want to restore VPS "${selectedInstance.hostname}" from snapshot "${snapshotName}"? This will stop the VPS and restore it to the snapshot state.`)) {
      return;
    }
    try {
      setActionLoading(`restore-${snapshotName}`);
      const response = await fetch(getApiUrl(`/admin/vps-backups/${selectedInstance.id}/restore`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ snapshotName })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`VPS restored from snapshot "${snapshotName}"`);
      } else {
        setError(data.error || 'Failed to restore from snapshot');
      }
    } catch (err) {
      setError('Failed to restore from snapshot');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteSnapshot = async (snapshotName: string) => {
    if (!selectedInstance) return;
    if (!window.confirm(`Are you sure you want to delete snapshot "${snapshotName}"?`)) {
      return;
    }
    try {
      setActionLoading(`delete-${snapshotName}`);
      const response = await fetch(getApiUrl(`/admin/vps-backups/${selectedInstance.id}/snapshots`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ snapshotName })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Snapshot "${snapshotName}" deleted`);
        fetchSnapshots(selectedInstance.id);
        fetchBackupStats();
      } else {
        setError(data.error || 'Failed to delete snapshot');
      }
    } catch (err) {
      setError('Failed to delete snapshot');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading VPS instances...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">VPS Backup Management</h2>
        <div className="text-sm text-gray-500">
          Max 3 snapshots per VPS (oldest auto-deleted when limit reached)
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Backup Stats */}
      {backupStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Backups</div>
            <div className="text-2xl font-bold text-gray-900">{backupStats.total_backups}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Size</div>
            <div className="text-2xl font-bold text-gray-900">
              {backupStats.total_size_mb > 1024
                ? `${(backupStats.total_size_mb / 1024).toFixed(2)} GB`
                : `${backupStats.total_size_mb} MB`}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">VPS with Backups</div>
            <div className="text-2xl font-bold text-gray-900">{backupStats.vps_with_backup}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Last Backup Run</div>
            <div className="text-lg font-bold text-gray-900">
              {backupStats.last_backup_run
                ? new Date(backupStats.last_backup_run).toLocaleString()
                : 'Never'}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* VPS List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold">VPS Instances</h3>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {vpsInstances.length === 0 ? (
              <div className="p-4 text-gray-500 text-center">No running VPS instances found</div>
            ) : (
              vpsInstances.map((instance) => (
                <div
                  key={instance.id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedInstance?.id === instance.id ? 'bg-blue-50' : ''}`}
                  onClick={() => handleSelectInstance(instance)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{instance.hostname || instance.name}</div>
                      <div className="text-sm text-gray-500">{instance.customer_email}</div>
                      <div className="text-xs text-gray-400">VMID: {instance.proxmox_vmid}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {instance.backup_enabled ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Auto-Backup ON
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Auto-Backup OFF
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          instance.backup_enabled
                            ? handleDisableBackup(instance.id)
                            : handleEnableBackup(instance.id);
                        }}
                        disabled={actionLoading?.includes(instance.id)}
                        className={`px-2 py-1 rounded text-xs ${
                          instance.backup_enabled
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        } disabled:opacity-50`}
                      >
                        {actionLoading?.includes(instance.id) ? '...' : (instance.backup_enabled ? 'Disable' : 'Enable')}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Snapshots Panel */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              {selectedInstance ? `Snapshots - ${selectedInstance.hostname}` : 'Select a VPS'}
            </h3>
            {selectedInstance && (
              <button
                onClick={() => setShowCreateSnapshot(true)}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
              >
                + Create Snapshot
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {!selectedInstance ? (
              <div className="p-4 text-gray-500 text-center">
                Select a VPS instance to view and manage snapshots
              </div>
            ) : loadingSnapshots ? (
              <div className="p-4 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : snapshots.length === 0 ? (
              <div className="p-4 text-gray-500 text-center">No snapshots found</div>
            ) : (
              snapshots.map((snapshot) => (
                <div key={snapshot.name} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{snapshot.name}</div>
                      <div className="text-sm text-gray-500">{snapshot.description || 'No description'}</div>
                      <div className="text-xs text-gray-400">
                        Created: {formatDate(snapshot.snaptime)}
                        {snapshot.vmstate && ' (includes RAM state)'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRestoreSnapshot(snapshot.name)}
                        disabled={actionLoading === `restore-${snapshot.name}`}
                        className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs hover:bg-yellow-200 disabled:opacity-50"
                      >
                        {actionLoading === `restore-${snapshot.name}` ? '...' : 'Restore'}
                      </button>
                      <button
                        onClick={() => handleDeleteSnapshot(snapshot.name)}
                        disabled={actionLoading === `delete-${snapshot.name}`}
                        className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 disabled:opacity-50"
                      >
                        {actionLoading === `delete-${snapshot.name}` ? '...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Snapshot Modal */}
      {showCreateSnapshot && selectedInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Create Snapshot</h3>
              <p className="text-sm text-gray-500">Create a snapshot for {selectedInstance.hostname}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Snapshot Name (optional)
                </label>
                <input
                  type="text"
                  value={newSnapshotName}
                  onChange={(e) => setNewSnapshotName(e.target.value)}
                  placeholder="auto-generated if empty"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newSnapshotDescription}
                  onChange={(e) => setNewSnapshotDescription(e.target.value)}
                  placeholder="Enter a description for this snapshot"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateSnapshot(false);
                  setNewSnapshotName('');
                  setNewSnapshotDescription('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSnapshot}
                disabled={actionLoading === 'create-snapshot'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === 'create-snapshot' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  'Create Snapshot'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VPSBackupManagement;
